<script>

/**
 * @typedef {object} NavbarItem
 * @property {string} name_key
 * @property {string | undefined} icon_key
 * @property {boolean} is_shown
 * @property {boolean} is_open
 * @property {string | undefined} path
 * @property {NavbarItem[] | undefined} children
 */

export default {
    props: {
        /**
         * @type {NavbarItem[]}
         */
        items: {
            type: Array,
            required: true,
        }
    },

    computed: {
        shown_items() {
            return this.items.filter(x => x.is_shown);
        },

        active_route() {
            return this.$route;
        }
    },

    beforeMount() {
        if (this.$route.query.parent) {
            this.items[Number(this.$route.query.parent)].is_open = true;
        }
    },

    methods: {
        /**
         * @param {NavbarItem} item 
         * @returns {boolean}
         */
        isActiveRoute(item) {
            if (!this.active_route || !item.path) return false;
            return this.active_route.fullPath === item.path;
        },
    }
}
</script>

<template>
    <div class="sust-sidebar-main flex-all-centered-col">
        <div v-for="(item, i) in shown_items" :key="i"
            class="sust-sidebar-item flex-all-centered-col"
        >
            <div class="sust-sidebar-body flex-all-centered-row"
                :is-active="isActiveRoute(item)"
            >
                <router-link v-if="item.children.length === 0"
                    :to="item.path"
                    class="router-link"
                >
                    <div v-if="item.icon_key">
                        <i :class="item.icon_key"></i>
                    </div>
                    <div>
                        {{ $t(`route_names.admin_panel.children.${item.name_key}`) }}
                    </div>
                </router-link>

                <div v-else 
                    @click="() => item.is_open = !item.is_open"
                >
                    <div v-if="item.icon_key">
                        <i :class="item.icon_key"></i>
                    </div>
                    <div>
                        {{ $t(`route_names.admin_panel.children.${item.name_key}`) }}
                    </div>
                </div>
            </div>

            <div v-if="item.is_open"
                class="sust-sidebar-item children-wrapper flex-all-centered-col"
            >
                <div v-for="(child, j) in item.children.filter(x => x.is_shown)" :key="j"
                    class="sust-sidebar-body child-item flex-all-centered-row"
                    :style="{
                        'animation-delay': `${(j+1)*(0.05)}s`
                    }"
                    :is-active="isActiveRoute(child)"
                >
                    <router-link 
                        :to="child.path"
                        class="router-link"
                    >
                        <div v-if="child.icon_key">
                            <i :class="child.icon_key"></i>
                        </div>
                        <div>
                            {{ $t(`route_names.admin_panel.children.${child.name_key}`) }}
                        </div>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .sust-sidebar-main {
        background-color: var(--sustainfinity-navy);
        color: whitesmoke;
        height: 100% !important;
        justify-content: flex-start;
        user-select: none !important;
        overflow-y: auto;
    }

    .sust-sidebar-item {
        border-bottom: .001em solid whitesmoke;
        
        > .sust-sidebar-body {
            justify-content: flex-start;

            > * {
                width: 100%;
                padding: .75em 1em;
            }
    
            width: 100%;
            text-wrap: nowrap;
    
            cursor: pointer;
            transition: background-color 150ms;
            
            &:hover {
                background-color: rgba(0, 0, 0, 0.136);
            }

            &[is-active] {
                cursor: default !important;
                pointer-events: none !important;
                background-color: rgba(0, 0, 0, 0.136) !important;
            }
        }
    }

    .router-link {
        color: whitesmoke !important;
        text-decoration: none !important;
    }

    .children-wrapper {
        transform-origin: top center;
        animation: OpenChildrenWrapper 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;

        >.child-item {
            border-bottom-color: lightgray;
            border-bottom-width: thin;
            border-bottom-style: dotted;
            text-wrap: wrap;
            padding-left: 2.5em;
            opacity: 0;
            transform: translateX(-10%);
            animation: FadeInChildItem 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
    }
    
    @keyframes OpenChildrenWrapper {
        from {
            transform: scaleY(0);
        } to {
            transform: scaleY(1);
        }
    }
    
    @keyframes FadeInChildItem {
        from {
            transform: translateX(-10%);
            opacity: 0;
        } to {
            transform: translateX(0);
            opacity: 1;
        }
    }

</style>